import { supabase } from "@/integrations/supabase/client";

export interface StudentWithProfile {
  student_id: string;
  profiles: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

export interface ClassWithStudents {
  id: string;
  name: string;
  description: string | null;
  students: StudentWithProfile[];
}

export async function getTeacherClasses(userId: string): Promise<string[]> {
    const { data: classesIds, error: classesIdsError } = await supabase
    .from("class_teachers")
    .select("class_id")
    .eq("teacher_id", userId);


  if (classesIdsError) {
    console.error("[getTeacherClasses] Error fetching classes ids:", classesIdsError);
    throw classesIdsError;
  }

  if (!classesIds || classesIds.length === 0) {
    console.log("[getTeacherClasses] No classes ids found");
    return [];
  }
  return classesIds.map((c) => c.class_id);
}

export async function getTeacherClassesWithStudents(): Promise<ClassWithStudents[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    console.log("[getTeacherClassesWithStudents] No user found");
    return [];
  }

  const classIds = await getTeacherClasses(user.id);
  if (classIds.length === 0) {
    return [];
  }

  const { data: classes, error: classesError } = await supabase
    .from("teacher_classes")
    .select("id, name, description")
    .in("id", classIds);

  if (classesError) {
    console.error("[getTeacherClassesWithStudents] Error fetching classes:", classesError);
    throw classesError;
  }

  if (!classes || classes.length === 0) {
    console.log("[getTeacherClassesWithStudents] No classes found");
    return [];
  }

  const classesWithStudents: ClassWithStudents[] = [];

  for (const classItem of classes) {
    const { data: classStudents, error: studentsError } = await supabase
      .from("class_students")
      .select("student_id, class_id, id")
      .eq("class_id", classItem.id);

    if (studentsError) {
      console.error(
        `[getTeacherClassesWithStudents] Error fetching students for class ${classItem.id}:`,
        studentsError
      );
      classesWithStudents.push({ ...classItem, students: [] });
      continue;
    }

    if (!classStudents || classStudents.length === 0) {
      classesWithStudents.push({ ...classItem, students: [] });
      continue;
    }

    const studentIds = classStudents.map((cs) => cs.student_id);

    const { data: profiles, error: profilesError } = await supabase
      .from("profiles")
      .select("id, display_name, first_name, last_name, is_test_student")
      .in("id", studentIds);

    if (profilesError) {
      console.error(`[getTeacherClassesWithStudents] Error fetching profiles:`, profilesError);
      classesWithStudents.push({ ...classItem, students: [] });
      continue;
    }

    const studentsWithProfiles: StudentWithProfile[] = classStudents.map((cs) => {
      const profile = profiles?.find((p) => p.id === cs.student_id);
      return {
        student_id: cs.student_id,
        profiles: profile
          ? {
              id: profile.id,
              display_name: profile.display_name,
              first_name: profile.first_name,
              last_name: profile.last_name,
            }
          : null,
      };
    });

    classesWithStudents.push({ ...classItem, students: studentsWithProfiles });
  }

  return classesWithStudents;
}